import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccessService } from '@app-core/services/access/access.service';
import { DataService } from '@app-core/services/data/data.service';
import { CreateDvrRequestBody } from '@app-trips/common/trips.model';
import { TripsService } from '@app-trips/services/trips.service';
import { finalize } from 'rxjs/operators';
import * as moment from 'moment';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import {
  STANDARD_DURATIONS,
  TIMELAPSE_DURATIONS,
  USAGE_MATRIX_STANDARD,
  USAGE_MATRIX_TIMELAPSE,
} from '@app-request-video/constants/request-video.constants';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

@Component({
  selector: 'app-request-confirmation',
  templateUrl: './request-confirmation.component.html',
  styleUrls: ['./request-confirmation.component.scss'],
})
export class RequestConfirmationComponent implements OnInit {
  public isSuccess = false;
  public isError = false;
  public loader = false;
  public mapImageSrc: string;
  public dataUsageEstimate: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<RequestConfirmationComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dataService: DataService,
    private router: Router,
    private accessService: AccessService,
    private tripsService: TripsService,
    private gtmService: GoogleTagManagerService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit() {
    this.dataUsageEstimate = this.getDataUsageEstimate();
    console.log();
  }

  public onSubmit() {
    this.isSuccess = false;
    this.isError = false;
    this.loader = true;
    const {
      videoResolution = '',
      enabledTimelapse = false,
      tripDetails = {},
      selectedTimeUTC,
      dvrDuration,
      dvrVideoType = 'road',
      dvrVideoTypes,
    } = this.data || {};

    const { tripId = '', driverId = '', endTimeUTC } = tripDetails || {};
    this.gtmService.confirmVideoRequestInRequestVideoPage(driverId);
    const { loginName: userEmail, name: userName, loginType, userId, userHash } = this.accessService.getLoginInfo();
    let selectedEndTimeUTC = moment(selectedTimeUTC).add(dvrDuration, 'seconds').toISOString();
    selectedEndTimeUTC = new Date(selectedEndTimeUTC) > new Date(endTimeUTC) ? endTimeUTC : selectedEndTimeUTC;

    const videoType =
      dvrVideoType === 'pictureInPictureRoadSide' || dvrVideoType === 'pictureInPictureDriverSide' ? 'pictureInPicture' : dvrVideoType;

    let dvrVideoTypeMainFrame = 'road';
    if (dvrVideoType === 'pictureInPictureRoadSide') {
      dvrVideoTypeMainFrame = 'road';
    } else if (dvrVideoType === 'pictureInPictureDriverSide') {
      dvrVideoTypeMainFrame = 'driver';
    }

    const body = new CreateDvrRequestBody({
      tripId,
      driverId,
      startTimeUTC: selectedTimeUTC,
      endTimeUTC: selectedEndTimeUTC,
      videoResolution,
      enabledTimelapse,
      dvrVideoType: videoType,
      dvrVideoTypeMainFrame,
      ...(dvrVideoType === 'separate' ? { driverCameraVideoResolution: videoResolution } : {}),
      ...(dvrVideoType === 'pictureInPicture' ? { timelapseCaptureInterval: 1, timelapseDisplayInterval: 50, dvrVideoTypeMainFrame } : {}),
      dvrInitiatedMetadata: {
        userType: loginType, // this loginType is changed for notification purpose
        email: userEmail,
        name: userName,
        ...(userHash ? { ssouserHash: userHash } : { userId }),
      },
      dvrVideoTypes,
    });
    this.tripsService
      .createDvrRequest(body)
      .pipe(
        finalize(() => {
          this.loader = false;
        })
      )
      .subscribe(
        () => {
          this.isSuccess = true;
          window.CommandBar.trackEvent('VIDEO_REQUEST_CREATED', {});
          this.mixpanelService.track('Video Request Submitted', {
            'Video Resolution': videoResolution,
            'Trip Id': tripId,
            'Driver Id': driverId,
            'DVR Duration': dvrDuration,
            'DVR VideoType': videoType,
            'UTC Time': selectedTimeUTC,
            'Local Time': selectedEndTimeUTC,
            'Submission Status': 'success',
          });
        },
        () => {
          this.isError = true;
          this.mixpanelService.track('Video Request Submissions Failed', {
            'Video Resolution': videoResolution,
            'Trip Id': tripId,
            'Driver Id': driverId,
            'DVR Duration': dvrDuration,
            'DVR VideoType': videoType,
            'UTC Time': selectedTimeUTC,
            'Local Time': selectedEndTimeUTC,
            'Submission Status': 'success',
          });
        }
      );
  }

  public onClose() {
    this.gtmService.requestAnotherVideoOnSuccessfulRequestVideoPage();
    this.dialogRef.close('SUCCESS');
  }

  public navigateToVideoRequests() {
    this.gtmService.gotoVideoRequestFromSuccessfullRequestVideoConfirm();
    this.dialogRef.close();
    this.router.navigate(['/video-requests']);
  }

  private getDataUsageEstimate() {
    const resolution = this.data?.videoResolution;
    const videoType = this.data?.dvrVideoType;
    let requestedDuration = this.data?.dvrDuration;
    const isTimelapse = this.data?.enabledTimelapse;

    if (!resolution || !videoType || requestedDuration == null) return null;

    let formatCategory = '';
    if (['road', 'driver'].includes(videoType)) {
      formatCategory = 'road';
    } else if (['separate', 'sideBySide'].includes(videoType)) {
      formatCategory = 'separate';
    } else if (['pictureInPicture', 'pictureInPictureRoadSide', 'pictureInPictureDriverSide'].includes(videoType)) {
      formatCategory = 'pictureInPicture';
    } else {
      return null;
    }

    const selectedDurationFormat = isTimelapse ? TIMELAPSE_DURATIONS : STANDARD_DURATIONS;
    const selectedUsageMatrix = isTimelapse ? USAGE_MATRIX_TIMELAPSE : USAGE_MATRIX_STANDARD;

    const maxDuration = selectedDurationFormat[selectedDurationFormat.length - 1];
    if (requestedDuration > maxDuration) {
      requestedDuration = maxDuration;
    }
    let durationIndex = selectedDurationFormat.findIndex((d) => d === requestedDuration);
    if (durationIndex === -1) {
      durationIndex = selectedDurationFormat.findIndex((d) => d >= requestedDuration);
      if (durationIndex === -1) durationIndex = selectedDurationFormat.length - 1;
    }

    const usageMatrixRow = selectedUsageMatrix?.[resolution]?.[formatCategory];
    if (!usageMatrixRow || durationIndex >= usageMatrixRow.length) {
      durationIndex = usageMatrixRow?.length ? usageMatrixRow.length - 1 : 0;
    }

    const estimatedUsageMb = usageMatrixRow?.[durationIndex];
    return estimatedUsageMb != null ? `${estimatedUsageMb.toFixed(2)} MB` : null;
  }
}
